import { Box, HStack, Image, useDisclosure, VStack } from '@chakra-ui/react';
import {
  Bookmark,
  Settings,
  LogOut,
  File,
  MessageSquare,
  Globe,
  Folder as FolderIcon,
  BookOpen,
  Search,
  Home,
  Delete,
  Share2,
} from 'react-feather';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAppSelector } from '../../lib/hooks';
import { useAuth } from 'oidc-react';
import { Folder, GraphView } from '../../lib/state/ViewTypes';
import { useState } from 'react';
import DeleteFolderDialog from '../Dialogs/DeleteFolderDialog';
import DeleteGraphDialog from '../Dialogs/DeleteGraphDialog';
import NavbarRoot from './NavbarRoot';
import {
  NavbarAccordionChild,
  NavbarItem,
  NavbarItemAccordion,
  NavbarItemProps,
} from './NavbarItem';
import { Tooltip } from '../ui/tooltip';

type navItemProps = {
  text: string;
  tooltip: string;
  path: string;
  icon: any;
  tabIndex: number;
};

const navItems: { [key: string]: navItemProps } = {
  // dashboard: {
  //   text: 'Dashboard',
  //   tooltip: 'Go to Dashboard (WIP)',
  //   path: '/dashboard',
  //   icon: Home,
  //   tabIndex: 100,
  // },
  home: {
    text: 'Home',
    tooltip: 'MESHR home page',
    path: '/home',
    icon: Home,
    tabIndex: 100,
  },
  search: {
    text: 'Search',
    tooltip: 'Browse and search for documents',
    path: '/research',
    icon: Search,
    tabIndex: 200,
  },
  projects: {
    text: 'Projects',
    tooltip: 'See and manage ypur projects',
    path: '/projects',
    icon: File,
    tabIndex: 300,
  },
  resources: {
    text: 'Resources',
    tooltip: 'See resources from the Council of Europe Knowledge Sharing Platform',
    path: '/resources',
    icon: BookOpen,
    tabIndex: 400,
  },
  documents: {
    text: 'Documents',
    tooltip: 'View your saved documents',
    path: '/saved_cases',
    icon: Bookmark,
    tabIndex: 500,
  },
  graph: {
    text: 'Graph',
    tooltip: 'View your saved graphs',
    path: '/graphs',
    icon: Globe,
    tabIndex: 600,
  },
  feedback: {
    text: 'Feedback',
    tooltip: 'This will redirect you to an external evaluation form ',
    path: '/feedback',
    icon: MessageSquare,
    tabIndex: 700,
  },
  settings: {
    text: 'Settings',
    tooltip: 'Configure your user settings',
    path: '/settings',
    icon: Settings,
    tabIndex: 800,
  },
  logout: {
    text: 'Log out',
    tooltip: 'Log out of your current session',
    path: '/log_out',
    icon: LogOut,
    tabIndex: 900,
  },
};

export default function Navbar({ isOpen, onToggle }: { isOpen: boolean; onToggle: () => void }) {
  const graphViews = useAppSelector((state) => state.viewReducer.documents.structure.GRAPH);
  const folders = useAppSelector((state) => state.viewReducer.documents.structure.FOLDER);

  const navigate = useNavigate();
  const location = useLocation();
  const auth = useAuth();

  const [folderInFocus, setFolderInFocus] = useState('');
  const [graphInFocus, setGraphInFocus] = useState('');
  const deleteFolderDialogDisclosure = useDisclosure();
  const deleteGraphDialogDisclosure = useDisclosure();

  function setNavItemProps(item: navItemProps): NavbarItemProps {
    return {
      text: item.text,
      icon: item.icon,
      tooltip: item.tooltip,
      tabIndex: item.tabIndex,
      isActive: location.pathname.includes(item.path),
      isOpen: isOpen,
      onClick: () => {
        navigate(item.path);
      },
    };
  }

  return (
    <NavbarRoot isOpen={isOpen}>
      <Tooltip
        content="Click me to shrink/expand the navigation bar"
        positioning={{ placement: 'right' }}
      >
        <Box
          position="relative"
          justifyItems="center"
          width="100%"
          textAlign="center"
          onClick={onToggle}
          _hover={{ background: 'navButtonHover', cursor: 'pointer' }}
          borderRadius="0.375rem"
        >
          <Image
            src={isOpen ? '/meshr_full_logo.png' : '/logo192_o.png'}
            display="block"
            borderRadius="0.8rem"
            height="16"
            minWidth="16"
            userSelect="none"
          />
        </Box>
      </Tooltip>

      <VStack height="100%" width="100%" justifyContent="space-between">
        <VStack width="100%">
          <NavbarItem {...setNavItemProps(navItems.home)} />

          <NavbarItem {...setNavItemProps(navItems.search)} />

          <NavbarItem {...setNavItemProps(navItems.projects)} />

          <NavbarItem {...setNavItemProps(navItems.resources)} />

          <NavbarItemAccordion {...setNavItemProps(navItems.documents)}>
            {Object.values(folders).map((folder: Folder, i) => (
              <NavbarAccordionChild
                key={`folder_button_${folder.id}`}
                text={folder.name}
                tooltip=""
                icon={FolderIcon}
                isActive={location.pathname === `${navItems.documents.path}/${folder.id}`}
                isOpen={isOpen}
                tabIndex={navItems.graph.tabIndex + 20 + 10 * i}
                onClick={() => navigate(`${navItems.documents.path}/${folder.id}`)}
                viewId={folder.id}
                options={[
                  {
                    text: 'Delete',
                    icon: <Delete />,
                    onClick: () => {
                      setFolderInFocus(folder.id);
                      deleteFolderDialogDisclosure.onOpen();
                    },
                  },
                ]}
              />
            ))}
          </NavbarItemAccordion>

          <DeleteFolderDialog
            isOpen={deleteFolderDialogDisclosure.open}
            onClose={deleteFolderDialogDisclosure.onClose}
            folderId={folderInFocus}
          />

          <NavbarItemAccordion {...setNavItemProps(navItems.graph)}>
            {Object.values(graphViews).map((view: GraphView, i) => (
              <NavbarAccordionChild
                key={`graph_button_${view.id}`}
                text={view.name}
                tooltip=""
                icon={Share2}
                isActive={location.pathname === `${navItems.graph.path}/${view.id}`}
                isOpen={isOpen}
                tabIndex={navItems.graph.tabIndex + 20 + 10 * i}
                onClick={() => navigate(`${navItems.graph.path}/${view.id}`)}
                viewId={view.id}
                options={[
                  {
                    text: 'Delete',
                    icon: <Delete />,
                    onClick: () => {
                      setGraphInFocus(view.id);
                      deleteGraphDialogDisclosure.onOpen();
                    },
                  },
                ]}
              />
            ))}
          </NavbarItemAccordion>

          <DeleteGraphDialog
            isOpen={deleteGraphDialogDisclosure.open}
            onClose={deleteGraphDialogDisclosure.onClose}
            graphId={graphInFocus}
          />
        </VStack>

        <VStack width="100%">
          <NavbarItem
            {...setNavItemProps(navItems.feedback)}
            onClick={() => window.open('https://nettskjema.no/a/463244')?.focus()}
          />
          <NavbarItem {...setNavItemProps(navItems.settings)} />
          <NavbarItem
            {...setNavItemProps(navItems.logout)}
            onClick={() => auth.signOutRedirect()}
          />
        </VStack>
      </VStack>
    </NavbarRoot>
  );
}
